import { useEffect, useRef, useState } from "react";
import { GameView } from "@pj/engine";
import { LocalRoom } from "@pj/sim";
import { todayKey } from "@pj/content";
import { expToReach } from "@pj/state";
import type { RoomEvent } from "@pj/protocol";
import { clearSave, loadSave, persistSave } from "./save";
import { SudokuOverlay } from "./SudokuOverlay";

interface ObjectiveHud {
  levelNum: number;
  levelName: string;
  label: string;
  progress: number;
  total: number;
  complete: boolean;
}

interface Hud {
  power: number;
  exp: number;
  expFloor: number;
  expForNext: number;
  abilities: string[];
  surfing: boolean;
  objective: ObjectiveHud | null;
  /** Level-2 countdown in ms (null = hidden). */
  timerMs: number | null;
  /** The open sudoku minigame (null = closed). */
  sudoku: { seed: number; entries: string } | null;
  /** The open Gen-3 message box (null = closed). */
  message: { lines: string[]; more: boolean } | null;
  /** The open yes/no prompt (null = closed). */
  prompt: { lines: string[] } | null;
  toast: string | null;
}

const INITIAL_HUD: Hud = {
  power: 1,
  exp: 0,
  expFloor: 0,
  expForNext: 100,
  abilities: [],
  surfing: false,
  objective: null,
  timerMs: null,
  sudoku: null,
  message: null,
  prompt: null,
  toast: null,
};

function formatTimer(ms: number): string {
  const total = Math.ceil(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

/**
 * Mounts the Pixi world, runs a LocalRoom over the game's Levels, and maps the
 * room's events onto a React HUD. Swapping LocalRoom for a networked
 * RemoteRoom later changes only the room construction; the HUD is event-driven
 * and untouched.
 */
export function GameCanvas(): JSX.Element {
  const ref = useRef<HTMLDivElement>(null);
  const roomRef = useRef<LocalRoom | null>(null);
  const [hud, setHud] = useState<Hud>(INITIAL_HUD);
  const [promptYes, setPromptYes] = useState(true);
  // Mirror of `promptYes` readable from DOM handlers without stale closures.
  const promptYesRef = useRef(true);
  const selectYes = (v: boolean): void => {
    promptYesRef.current = v;
    setPromptYes(v);
  };

  useEffect(() => {
    const parent = ref.current;
    if (!parent) return;

    const dateKey = todayKey();
    const save = loadSave();
    const room = new LocalRoom({ save, dateKey, onPersist: () => persistSave(save) });
    roomRef.current = room;

    let toastTimer: ReturnType<typeof setTimeout> | null = null;

    const offEvent = room.onEvent((ev: RoomEvent) => {
      switch (ev.type) {
        case "progress":
          setHud((h) => ({
            ...h,
            exp: ev.exp,
            power: ev.power,
            expFloor: expToReach(ev.power),
            expForNext: ev.expForNext,
            abilities: ev.abilities,
          }));
          break;
        case "objective":
          setHud((h) => ({
            ...h,
            objective: {
              levelNum: ev.levelNum,
              levelName: ev.levelName,
              label: ev.label,
              progress: ev.progress,
              total: ev.total,
              complete: ev.complete,
            },
          }));
          break;
        case "ability":
          if (ev.ability === "surf") setHud((h) => ({ ...h, surfing: ev.active }));
          break;
        case "timer":
          setHud((h) => ({ ...h, timerMs: ev.remainingMs }));
          break;
        case "sudoku":
          setHud((h) => ({ ...h, sudoku: { seed: ev.seed, entries: ev.entries } }));
          break;
        case "sudokuClose":
          setHud((h) => ({ ...h, sudoku: null }));
          break;
        case "message":
          setHud((h) => ({ ...h, message: { lines: ev.lines, more: ev.more }, prompt: null }));
          break;
        case "prompt":
          promptYesRef.current = true;
          setPromptYes(true);
          setHud((h) => ({ ...h, prompt: { lines: ev.lines }, message: null }));
          break;
        case "uiClose":
          setHud((h) => ({ ...h, message: null, prompt: null }));
          break;
        case "toast":
          setHud((h) => ({ ...h, toast: ev.text }));
          if (toastTimer) clearTimeout(toastTimer);
          toastTimer = setTimeout(() => setHud((h) => ({ ...h, toast: null })), 2400);
          break;
        case "joined":
          break;
      }
    });

    let seq = 0;
    const view = new GameView({
      mapId: room.mapId,
      playerLabel: "poopie",
      onIntent: (dir) => room.send({ type: "intent", seq: ++seq, dir }),
      onInteract: () => room.send({ type: "interact" }),
    });

    let cancelled = false;
    void view.init(parent).then(() => {
      if (cancelled) return;
      const { actorId, snapshot } = room.join("Puranjay");
      view.applySnapshot(snapshot);
      view.setFollow(actorId);
      room.onDiff((d) => view.applyDiff(d));
      room.start();
    });

    return () => {
      cancelled = true;
      offEvent();
      if (toastTimer) clearTimeout(toastTimer);
      room.stop();
      view.destroy();
      roomRef.current = null;
    };
  }, []);

  // Yes/no prompt steals the keyboard while open (the field is locked anyway).
  const promptOpen = hud.prompt !== null;
  useEffect(() => {
    if (!promptOpen) return;
    const onKey = (e: KeyboardEvent): void => {
      const reply = (accept: boolean) => {
        roomRef.current?.send({ type: "promptReply", accept });
      };
      switch (e.code) {
        case "ArrowUp":
        case "ArrowDown":
        case "KeyW":
        case "KeyS":
          selectYes(!promptYesRef.current);
          break;
        case "Space":
        case "Enter":
        case "KeyZ":
          reply(promptYesRef.current);
          break;
        case "KeyX":
        case "Escape":
          reply(false);
          break;
        default:
          return; // let movement keys through (the sim ignores them while locked)
      }
      e.preventDefault();
      e.stopPropagation();
    };
    window.addEventListener("keydown", onKey, { capture: true });
    return () => window.removeEventListener("keydown", onKey, { capture: true });
  }, [promptOpen]);

  const advance = (): void => {
    roomRef.current?.send({ type: "interact" });
  };
  const reply = (accept: boolean): void => {
    roomRef.current?.send({ type: "promptReply", accept });
  };

  const span = Math.max(1, hud.expForNext - hud.expFloor);
  const expPct = Math.max(0, Math.min(1, (hud.exp - hud.expFloor) / span));
  const hasSurf = hud.abilities.includes("surf");
  const timerLow = hud.timerMs !== null && hud.timerMs < 5 * 60 * 1000;

  return (
    <div className="game-root">
      <div ref={ref} className="game-canvas" />

      <div className="hud">
        <div className="hud-top-left">
          <div className="lvl">Power {hud.power}</div>
          <div className="exp-bar">
            <div className="exp-fill" style={{ width: `${(expPct * 100).toFixed(1)}%` }} />
          </div>
          <div className="exp-text">
            {hud.exp} / {hud.expForNext} XP
          </div>
        </div>

        {hud.objective && (
          <div className={`hud-objective${hud.objective.complete ? " done" : ""}`}>
            <div className="obj-label">
              LEVEL {hud.objective.levelNum} — {hud.objective.levelName}
            </div>
            <div className="obj-task">
              {hud.objective.label}: {hud.objective.progress} / {hud.objective.total}
              {hud.objective.complete ? " ✓" : ""}
            </div>
          </div>
        )}

        {hud.timerMs !== null && (
          <div className={`hud-timer${timerLow ? " low" : ""}`}>⏰ {formatTimer(hud.timerMs)}</div>
        )}

        {hasSurf && (
          <div className="hud-abilities">
            <span className={`ability-chip${hud.surfing ? " active" : ""}`}>
              {hud.surfing ? "~ SURFING ~" : "SURF CHARM"}
            </span>
          </div>
        )}

        {hud.toast && <div className="hud-toast">{hud.toast}</div>}

        {hud.message && (
          <div className="msgbox" onClick={advance}>
            {hud.message.lines.map((line, i) => (
              <p key={i}>{line}</p>
            ))}
            <span className="msg-arrow">▼</span>
          </div>
        )}

        {hud.prompt && (
          <>
            <div className="msgbox">
              {hud.prompt.lines.map((line, i) => (
                <p key={i}>{line}</p>
              ))}
            </div>
            <div className="choicebox">
              <button
                type="button"
                className={`choice${promptYes ? " selected" : ""}`}
                onMouseEnter={() => selectYes(true)}
                onClick={() => reply(true)}
              >
                <span className="cursor">▶</span> YES
              </button>
              <button
                type="button"
                className={`choice${promptYes ? "" : " selected"}`}
                onMouseEnter={() => selectYes(false)}
                onClick={() => reply(false)}
              >
                <span className="cursor">▶</span> NO
              </button>
            </div>
          </>
        )}

        <div className="hud-controls">Arrows / WASD to move · SPACE to interact</div>

        <button
          type="button"
          className="hud-reset"
          onClick={() => {
            if (
              window.confirm("Reset all progress? Power, Surf and completed LEVELS will be cleared.")
            ) {
              clearSave();
              window.location.reload();
            }
          }}
        >
          ↺ Reset
        </button>
      </div>

      {hud.sudoku && (
        <SudokuOverlay
          seed={hud.sudoku.seed}
          entries={hud.sudoku.entries}
          onEntries={(entries) => roomRef.current?.send({ type: "sudokuEntries", entries })}
          onExit={() => roomRef.current?.send({ type: "sudokuExit" })}
        />
      )}
    </div>
  );
}
